import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const PROVENANCE_TYPE_VALUES = [
  "",
  "direct_artiste",
  "echange_production",
  "galerie",
  "vente_privee",
  "encheres",
  "succession",
  "don",
  "collection_privee",
  "autre",
];

/**
 * ProvenanceModal
 *
 * Props:
 *  - onClose: () => void
 *  - onAddProvenance: (item) => void   → création
 *  - onUpdateProvenance: (item) => void → édition (optionnel)
 *  - initialData: object|null          → si fourni, on est en mode édition
 */
const ProvenanceModal = ({
  onClose,
  onAddProvenance,
  onUpdateProvenance,
  initialData = null,
}) => {
  const { t } = useTranslation();
  const isEditing = !!initialData;

  const provenanceTypes = PROVENANCE_TYPE_VALUES.map((value) => ({
    value,
    label: t(`artworkModals.provenance.types.${value || "select"}`),
  }));

  const [type, setType] = useState(initialData?.type || "");
  const [date, setDate] = useState(
    initialData?.date
      ? initialData.date.slice(0, 10) // ISO → YYYY-MM-DD pour <input type="date">
      : ""
  );
  const [description, setDescription] = useState(
    initialData?.description || ""
  );
  const [location, setLocation] = useState(initialData?.location || "");

  // Si on change de mode édition en cours de vie du modal (cas rare mais défensif)
  useEffect(() => {
    if (initialData) {
      setType(initialData.type || "");
      setDate(initialData.date ? initialData.date.slice(0, 10) : "");
      setDescription(initialData.description || "");
      setLocation(initialData.location || "");
    }
  }, [initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!description.trim() && !type) return;

    const payload = {
      ...(isEditing ? { id: initialData.id } : {}),
      type: type || "",
      date: date || new Date().toISOString(),
      description: description.trim(),
      location: location.trim(),
    };

    if (isEditing && onUpdateProvenance) {
      onUpdateProvenance(payload);
    } else {
      onAddProvenance(payload);
    }

    onClose();
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6">
        <h3 className="text-lg font-semibold mb-5">
          {isEditing
            ? t("artworkModals.provenance.editTitle")
            : t("artworkModals.provenance.addTitle")}
        </h3>

        <form className="space-y-4" onSubmit={handleSubmit}>
          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("artworkModals.provenance.fields.type")}
            </label>
            <select
              value={type}
              onChange={(e) => {
                const v = e.target.value;
                setType(v);
                // Pré-remplir la description si elle est vide ou identique à un ancien type
                if (!description || provenanceTypes.some((item) => item.label === description)) {
                  const found = provenanceTypes.find((item) => item.value === v);
                  if (found && found.value) setDescription(found.label);
                }
              }}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
            >
              {provenanceTypes.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("artworkModals.provenance.fields.date")}
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("artworkModals.provenance.fields.description")}
              <span className="text-gray-400 font-normal ml-1">{t("artworkModals.provenance.required")}</span>
            </label>
            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              placeholder={t("artworkModals.provenance.placeholders.description")}
              required
            />
          </div>

          {/* Lieu */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("artworkModals.provenance.fields.location")}
            </label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              placeholder={t("artworkModals.provenance.placeholders.location")}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="text-sm px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              {t("artworkModals.common.cancel")}
            </button>
            <button
              type="submit"
              className="text-sm px-4 py-2 rounded-md bg-black text-white hover:bg-gray-800"
            >
              {isEditing ? t("artworkModals.common.save") : t("artworkModals.common.add")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProvenanceModal;